import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
} from '@tanstack/react-table';
import { useTranslation } from '@/lib/i18n';
import type { Money, ProductSummary } from '@/lib/api-admin/types';
import type { ToggleableProductColumn } from '@/stores/useProductColumnPrefs';
import { useNavigate } from 'react-router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Copy, ImageOff } from 'lucide-react';
import {
  Button,
  Checkbox,
  StatusDot,
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '@/components/ui';
import { buildStorefrontProductUrl } from '../lib/storefrontProductUrl';
import { formatAuditDate, formatAuditTime } from '../lib/auditTime';
import { AdminNameCell } from '@/features/admin/components/AdminNameCell';

// BE 정렬은 등록일·판매가뿐이다(lib/productSort). 대응 없는 열(상품명·발매일)은 눌러도 순서가 안 바뀌어 뺐다.
export const SORTABLE_COLUMNS = new Set(['salePrice', 'createdAt']);

export interface ProductTableProps {
  items: readonly ProductSummary[];
  sort: string;
  onSortChange: (sort: string) => void;
  /**
   * ID88: 다중 선택된 상품 id 집합. 체크박스 열이 노출되고, 헤더 체크박스로 현재 페이지
   * 전체 선택/해제한다. ID60 복사(top-bar)는 이 선택이 정확히 1건일 때만 활성.
   */
  selectedIds?: ReadonlyArray<string>;
  onToggleRow?: (productId: string) => void;
  /** ID88: 헤더 전체 선택/해제 — 인자는 새 선택 집합(전체 선택 시 현재 페이지 id, 해제 시 []). */
  onToggleAll?: (productIds: ReadonlyArray<string>) => void;
  /** ID60: 행 우측 [복사] 액션. 누르면 해당 상품 복사 → 새 상품 수정화면 라우팅. */
  onCopy?: (productId: string) => void;
  copyingId?: string | null;
  /**
   * ID89: 노출 항목 설정 — 운영자가 켠 선택(optional) 컬럼만 표시한다. 미지정(undefined)
   * 이면 모든 선택 컬럼을 노출(기존 동작 동일). name·select·actions 는 가시성 토글 대상 아님.
   */
  visibleColumns?: ReadonlyArray<ToggleableProductColumn>;
}

export const parseSort = (sort: string): { id: string; dir: 'ASC' | 'DESC' } => {
  const parts = sort.split(',');
  const id = parts[0] && parts[0].length > 0 ? parts[0] : 'createdAt';
  const dir = parts[1] === 'ASC' ? 'ASC' : 'DESC';
  return { id, dir };
};

export const toggleSort = (current: string, columnId: string): string => {
  const { id, dir } = parseSort(current);
  if (id !== columnId) return `${columnId},DESC`;
  return `${columnId},${dir === 'DESC' ? 'ASC' : 'DESC'}`;
};

const formatMoney = (money: Money | null | undefined): string => {
  if (!money || money.amount == null || Number.isNaN(money.amount)) return '—';
  const code = money.currencyCode ?? 'KRW';
  const symbol = code === 'KRW' ? '₩' : code === 'USD' ? '$' : code === 'JPY' ? '¥' : '';
  return `${symbol}${new Intl.NumberFormat('ko-KR').format(money.amount)}`;
};

const ARTIST_TYPE_LABEL: Record<string, string> = {
  SOLO: '솔로',
  GROUP: '그룹',
};

/**
 * QA-89 — 판매상태(saleStatus) 색(tone) 매핑. be-o ProductSummary.saleStatus = canonical ProductStatus
 * 6값(DRAFT/PENDING_REVIEW/SELLING/SOLD_OUT/STOPPED/ARCHIVED). ★표시 라벨은 BE가 동봉하는
 * saleStatusLabel(한글, raw enum 노출 방지 정본)을 그대로 쓰고, 여기서는 StatusDot 색만 enum 으로 도출한다.
 * 미정의 값은 중립색.
 */
const SALE_STATUS_TONE: Record<string, 'success' | 'danger' | 'neutral' | 'warn'> = {
  SELLING: 'success',
  SOLD_OUT: 'danger',
  // 판매중단(STOPPED)은 의도적 운영 일시중지 → 품절(danger)과 구분해 warn(be-o 권장 톤).
  STOPPED: 'warn',
  PENDING_REVIEW: 'warn',
  DRAFT: 'neutral',
  ARCHIVED: 'neutral',
};

/**
 * QA-89(R4 G) — 컬럼별 content-fit 최소폭(px). design-o 산식(qa4-design-ssot.md 부록②, 13px 셀+px-4 기준).
 * 표 min-width = 노출 컬럼 폭 합. flat 150/col(기본 10컬럼=1500>가용폭→오버스크롤)을 가중치로 교체:
 * 기본 10컬럼 합 ~1014(1440/1536 노스크롤) / 전체 15컬럼 ~1504(확장 시 스크롤). 상품명 240은 .sticky-col-1 정합.
 */
const COLUMN_MIN_WIDTH: Record<string, number> = {
  select: 44,
  // QA-244: 상품명 컬럼 폭 확대(200→280) — 길어 잘리던 상품명이 더 많이 보이도록 minWidth 만
  // 부여하고 셀은 growable(고정 width 제거)로 둔다. 2줄 clamp 는 유지(가로넘침 방지).
  // ★name minWidth 는 sticky-col-2 오프셋·tableMinWidth 합 산식의 기준 폭.
  // QA-482: 상품명을 줄이지 않고 다 보이므로 한 줄에 더 들어가게 폭을 넓힌다(3~4줄 이내).
  name: 320,
  optionCount: 50,
  productType: 100,
  artistName: 90,
  productCode: 110,
  // QA-231: 자체상품코드(앨범별 고정 코드) 컬럼 폭.
  selfProductCode: 120,
  barcode: 140,
  // QA-348: "노출 상태" 머리글·"미노출" 이 꺾이지 않는 폭.
  displayed: 96,
  saleStatus: 90,
  listPrice: 90,
  salePrice: 90,
  releaseDate: 110,
  // 등록일/수정일 두 줄(오늘 HH:mm, 지난 날 YYYY-MM-DD).
  createdAt: 100,
  totalStockQty: 80,
  isSoldOut: 80,
  // QA-R6 H: 상품 복사 컬럼(노출 항목 토글). actions 와 동일 폭.
  copy: 60,
  actions: 70,
};
/**
 * 머리글이 두 줄로 꺾이지 않게 할 컬럼. QA-348: 발매일·노출 상태·판매상태 추가(짧은 머리글이 좁은 칸에서 꺾였다).
 */
const NOWRAP_HEADER_COLUMNS = new Set(['createdAt', 'totalStockQty', 'isSoldOut', 'releaseDate', 'displayed', 'saleStatus']);

/** 알 수 없는 컬럼 id 의 보수적 폭(폴백). */
const DEFAULT_COLUMN_MIN_WIDTH = 100;

/**
 * QA-89(R4 G) — 가로 스크롤 중 좌측 식별 열 고정(design-o `.sticky-col-1`/`.sticky-col-2`).
 * select(체크박스)=col-1(left:0,44px), name(상품명)=col-2(left:44px). 그 외 빈 문자열.
 * ★left 오프셋(.sticky-col-2 left:44px)은 COLUMN_MIN_WIDTH.select=44 와 정합.
 */
const stickyColClass = (columnId: string): string =>
  columnId === 'select' ? 'sticky-col-1' : columnId === 'name' ? 'sticky-col-2' : '';

export const ProductTable = ({
  items,
  sort,
  onSortChange,
  selectedIds,
  onToggleRow,
  onToggleAll,
  onCopy,
  copyingId = null,
  visibleColumns,
}: ProductTableProps) => {
  const { t } = useTranslation('products');
  const navigate = useNavigate();
  const { id: activeSortId, dir: activeSortDir } = parseSort(sort);
  const selectable = Boolean(onToggleRow);
  const selectedSet = useMemo(() => new Set(selectedIds ?? []), [selectedIds]);
  // ID89: 선택 컬럼 가시성. undefined = 전부 노출(기존 동작). 토글 대상 컬럼만 검사.
  const isColumnVisible = useMemo(() => {
    const set = visibleColumns ? new Set(visibleColumns) : null;
    return (id: ToggleableProductColumn): boolean => set === null || set.has(id);
  }, [visibleColumns]);
  // QA-R6 H: 행 복사 컬럼은 onCopy 가 주어지고(능력) 노출 항목에서 'copy' 가 켜져 있을 때만 렌더.
  // 기본값에 copy 가 포함돼 있어 기존(항상 노출) 동작은 유지되고, 운영자가 끌 수 있게 된다.
  const copyable = Boolean(onCopy) && isColumnVisible('copy');
  const pageIds = useMemo(() => items.map((p) => p.productId), [items]);
  // 현재 페이지 전체가 선택된 상태인지(빈 페이지는 false → 헤더 체크 비활성 의미).
  const allSelected = pageIds.length > 0 && pageIds.every((id) => selectedSet.has(id));

  const columns = useMemo<ColumnDef<ProductSummary>[]>(
    () => [
      ...(selectable
        ? [
            {
              id: 'select',
              header: () => (
                <div className="flex items-center justify-center">
                  <Checkbox
                    checked={allSelected}
                    onCheckedChange={(next) => onToggleAll?.(next ? pageIds : [])}
                    aria-label={t('products.bulk.selectAll', { defaultValue: '전체 선택' })}
                  />
                </div>
              ),
              cell: ({ row }) => {
                const id = row.original.productId;
                const checked = selectedSet.has(id);
                return (
                  <div
                    className="flex items-center justify-center"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Checkbox
                      checked={checked}
                      onCheckedChange={() => onToggleRow?.(id)}
                      aria-label={t('products.bulk.selectRow', {
                        name: row.original.name,
                        defaultValue: row.original.name,
                      })}
                    />
                  </div>
                );
              },
            } satisfies ColumnDef<ProductSummary>,
          ]
        : []),
      {
        id: 'name',
        accessorKey: 'name',
        header: () => t('products.columns.name'),
        cell: ({ row }) => {
          const p = row.original;
          // QA-R6 H: 브랜드 미설정 시 '—' 가 "EVERLINE vs -" 혼란을 줬다 → 브랜드 없으면 줄 자체를 미노출.
          const brand = p.brandName?.trim();
          return (
            // QA-R6 H: 상품명 2줄 wrap 시 썸네일과 텍스트를 상단 정렬(items-start).
            <div className="flex items-start gap-3 min-w-0">
              {/* QA-129: 썸네일 클릭 → storefront 공개 상품 화면(새 탭). 행 클릭(수정 진입)과 분리. */}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(
                    buildStorefrontProductUrl(p.productId),
                    '_blank',
                    'noopener,noreferrer',
                  );
                }}
                title={t('products.list.openStorefront', { defaultValue: '프론트 상품 화면 열기 (새 탭)' })}
                aria-label={t('products.list.openStorefrontAria', {
                  name: p.name,
                  defaultValue: t('products.list.openStorefront', '프론트 상품 화면 열기 (새 탭)'),
                })}
                className="flex h-12 w-12 flex-shrink-0 items-center justify-center overflow-hidden rounded transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-2"
                style={{
                  background: 'var(--surface-1)',
                  border: '1px solid var(--divider-strong)',
                  cursor: 'pointer',
                }}
              >
                {p.thumbnailUrl ? (
                  <img
                    src={p.thumbnailUrl}
                    alt=""
                    className="h-full w-full object-cover"
                    loading="lazy"
                  />
                ) : (
                  <ImageOff size={16} style={{ color: 'var(--content-soft)' }} aria-hidden="true" />
                )}
              </button>
              <div className="flex min-w-0 flex-col">
                {/* QA-129: 상품명 클릭 → BO 수정 페이지. 행 클릭과 동일 목적지지만 명시 링크로 분리.
                    QA-482: 줄 수 제한 없이 상품명 전체를 보인다(음반 버전명이 뒤에 붙어 2줄 자르기로는 구분이 안 됐다, 카페24 목록처럼 행이 늘어남). */}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/products/${row.original.productId}`);
                  }}
                  title={p.name}
                  className="break-words text-left text-[13px] font-medium leading-snug hover:underline focus-visible:outline-none focus-visible:ring-2"
                  style={{ color: 'var(--content-strong)', cursor: 'pointer' }}
                >
                  {p.name}
                </button>
                {brand ? (
                  <span className="mt-0.5 text-[12px]" style={{ color: 'var(--content-muted)' }}>
                    {brand}
                  </span>
                ) : null}
              </div>
            </div>
          );
        },
      },
      // QA-231: 자체상품코드(self_product_code) — 앨범별 고정 코드. 재고를 자체상품코드별로 관리할 수 있게
      // 목록에 항상 노출한다(상품별 고유 productCode 컬럼과 별개). 미설정이면 '—'. raw UUID/식별자 미노출.
      {
        id: 'selfProductCode',
        header: () => t('products.columns.selfProductCode', '자체상품코드'),
        cell: ({ row }) => {
          const code = (row.original as { selfProductCode?: string | null }).selfProductCode;
          return (
            <span className="text-[13px] tabular-nums" style={{ color: 'var(--content-muted)' }}>
              {code && code.trim() !== '' ? code : '—'}
            </span>
          );
        },
      } satisfies ColumnDef<ProductSummary>,
      // QA-89(2차)+R4 H+QA-R6 H: 옵션 — 상품명 바로 뒤에 노출(요청 컬럼 순서). be-o ProductSummary.optionSummary
      //   (조합옵션 내용 "색상: …|사이즈: …") + optionCount(추가옵션+조합옵션 그룹 합산, 0=옵션없음).
      //   표시 규칙(lead): 내용 있으면 내용, 없으면 개수. optionSummary 有 → 그대로(길면 truncate+title).
      //   optionSummary 無 → optionCount 폴백(>=1 "N개" / 0 "없음" / null "—"). raw enum/식별자 미노출.
      ...(isColumnVisible('optionCount')
        ? [
            {
              id: 'optionCount',
              header: () => t('products.columns.optionCount', '옵션'),
              cell: ({ row }) => {
                const r = row.original as { optionCount?: number | null; optionSummary?: string | null };
                const summary = r.optionSummary?.trim();
                // 1) 조합옵션 내용 요약이 있으면 그대로(말줄임 + title 툴팁 — 긴 요약이 표를 늘리지 않게).
                if (summary) {
                  return (
                    <span
                      className="block max-w-[100px] truncate text-[13px]"
                      style={{ color: 'var(--content-default)' }}
                      title={summary}
                    >
                      {summary}
                    </span>
                  );
                }
                // 2) 요약 없음 → 개수 폴백.
                const count = r.optionCount;
                const label =
                  count == null
                    ? '—'
                    : count <= 0
                      ? t('products.optionSummary.none', '없음')
                      : t('products.optionSummary.count', { count, defaultValue: `${count}개` });
                return (
                  <span
                    className="whitespace-nowrap text-[13px] tabular-nums"
                    style={{
                      color: count != null && count > 0 ? 'var(--content-default)' : 'var(--content-muted)',
                    }}
                  >
                    {label}
                  </span>
                );
              },
            } satisfies ColumnDef<ProductSummary>,
          ]
        : []),
      ...(isColumnVisible('productType')
        ? [
            {
              id: 'productType',
              accessorKey: 'productType',
              header: () => t('products.columns.productType'),
              cell: ({ row }) => (
                <span
                  className="inline-flex items-center whitespace-nowrap rounded px-2 py-0.5 text-[12px] font-medium"
                  style={{
                    background: 'var(--surface-1)',
                    color: 'var(--content-default)',
                    border: '1px solid var(--divider-strong)',
                  }}
                >
                  {t(`products.types.${row.original.productType}`, String(row.original.productType))}
                </span>
              ),
            } satisfies ColumnDef<ProductSummary>,
          ]
        : []),
      ...(isColumnVisible('artistName')
        ? [
            {
              id: 'artistName',
              header: () => t('products.columns.artistName'),
              cell: ({ row }) => {
                const name = row.original.artistName ?? '—';
                // 유형은 라벨이 있는 값(솔로·그룹)만 보인다. UNKNOWN(고도몰에 구분 원천이 없어 이관 아티스트 전원,
                // 운영자가 나중에 재분류)이나 새로 생긴 값은 둘째 줄을 그리지 않는다 — 영문 원값이 새지 않게.
                const typeLabel = row.original.artistType ? ARTIST_TYPE_LABEL[row.original.artistType] : undefined;
                return (
                  <div className="flex max-w-[90px] flex-col">
                    <span
                      className="truncate text-[14px]"
                      style={{ color: 'var(--content-default)' }}
                      title={name}
                    >
                      {name}
                    </span>
                    {typeLabel ? (
                      <span className="text-[11px]" style={{ color: 'var(--content-muted)' }}>
                        {typeLabel}
                      </span>
                    ) : null}
                  </div>
                );
              },
            } satisfies ColumnDef<ProductSummary>,
          ]
        : []),
      // QA-89/QA-231: 상품코드 — admin 응답 ProductSummary.productCode(서버 자동배정 고유값).
      // 자체상품코드(앨범별 고정)는 위 selfProductCode 컬럼에서 별도 노출. generated 미반영 → 안전 캐스트.
      ...(isColumnVisible('productCode')
        ? [
            {
              id: 'productCode',
              header: () => t('products.columns.productCode', '상품코드'),
              cell: ({ row }) => {
                const code = (row.original as { productCode?: string | null }).productCode;
                return (
                  <span className="text-[13px] tabular-nums" style={{ color: 'var(--content-muted)' }}>
                    {code && code.trim() !== '' ? code : '—'}
                  </span>
                );
              },
            } satisfies ColumnDef<ProductSummary>,
          ]
        : []),
      // QA-89: 바코드 — admin 응답 ProductSummary.barcode(nullable).
      ...(isColumnVisible('barcode')
        ? [
            {
              id: 'barcode',
              header: () => t('products.columns.barcode', '바코드'),
              cell: ({ row }) => {
                const code = (row.original as { barcode?: string | null }).barcode;
                return (
                  <span className="text-[13px] tabular-nums" style={{ color: 'var(--content-muted)' }}>
                    {code && code.trim() !== '' ? code : '—'}
                  </span>
                );
              },
            } satisfies ColumnDef<ProductSummary>,
          ]
        : []),
      // QA-89+QA-R6 H: 노출상태(진열여부) — 바코드 뒤에 노출-판매상태 순으로 배치(요청 컬럼 순서).
      // admin 응답 ProductSummary.displayed(boolean). 판매상태(isSoldOut/saleStatus)와 별개 축: 전시 노출 on/off.
      ...(isColumnVisible('displayed')
        ? [
            {
              id: 'displayed',
              header: () => t('products.columns.displayed', '노출상태'),
              cell: ({ row }) => {
                const displayed = (row.original as { displayed?: boolean }).displayed ?? false;
                return (
                  <StatusDot
                    className="whitespace-nowrap"
                    tone={displayed ? 'success' : 'neutral'}
                    label={
                      displayed
                        ? t('products.columns.displayedShown', '노출')
                        : t('products.columns.displayedHidden', '미노출')
                    }
                  />
                );
              },
            } satisfies ColumnDef<ProductSummary>,
          ]
        : []),
      // QA-89(2차)+QA-R6 H: 판매상태 — 노출상태 뒤(요청 순서). be-o ProductSummary.saleStatus(canonical ProductStatus)
      // + saleStatusLabel(한글). ★표시는 BE 동봉 saleStatusLabel(raw enum 노출 방지 정본) 그대로, 색만 enum 도출.
      ...(isColumnVisible('saleStatus')
        ? [
            {
              id: 'saleStatus',
              header: () => t('products.columns.saleStatus', '판매상태'),
              cell: ({ row }) => {
                const r = row.original as { saleStatus?: string | null; saleStatusLabel?: string | null };
                const label = r.saleStatusLabel;
                // BE 라벨이 없거나 '-'(BE 미매핑 정본)면 em-dash. raw enum 은 절대 표시하지 않는다.
                if (!label || label.trim() === '' || label.trim() === '-') {
                  return (
                    <span className="text-[13px]" style={{ color: 'var(--content-muted)' }}>
                      —
                    </span>
                  );
                }
                const tone = (r.saleStatus ? SALE_STATUS_TONE[r.saleStatus] : undefined) ?? 'neutral';
                return (
                  <span className="whitespace-nowrap">
                    <StatusDot tone={tone} label={label} />
                  </span>
                );
              },
            } satisfies ColumnDef<ProductSummary>,
          ]
        : []),
      ...(isColumnVisible('listPrice')
        ? [
            {
              id: 'listPrice',
              header: () => t('products.columns.listPrice', '정가'),
              cell: ({ row }) => (
                <span
                  className="text-[14px] tabular-nums"
                  style={{ color: 'var(--content-muted)' }}
                >
                  {formatMoney(row.original.listPrice)}
                </span>
              ),
            } satisfies ColumnDef<ProductSummary>,
          ]
        : []),
      ...(isColumnVisible('salePrice')
        ? [
            {
              id: 'salePrice',
              header: () => t('products.columns.salePrice'),
              cell: ({ row }) => {
                const p = row.original;
                const hasDiscount = (p.discountRate ?? 0) > 0;
                return (
                  <div className="flex flex-col items-end">
                    <span
                      className="text-[14px] font-semibold tabular-nums"
                      style={{ color: 'var(--content-strong)' }}
                    >
                      {formatMoney(p.salePrice)}
                    </span>
                    {hasDiscount ? (
                      <span className="flex items-center gap-1.5 text-[12px] tabular-nums">
                        <span style={{ color: 'var(--signal-danger)', fontWeight: 600 }}>
                          {p.discountRate}%
                        </span>
                        <span style={{ color: 'var(--content-muted)', textDecoration: 'line-through' }}>
                          {formatMoney(p.listPrice)}
                        </span>
                      </span>
                    ) : null}
                  </div>
                );
              },
            } satisfies ColumnDef<ProductSummary>,
          ]
        : []),
      ...(isColumnVisible('releaseDate')
        ? [
            {
              id: 'releaseDate',
              header: () => t('products.columns.releaseDate'),
              cell: ({ row }) => (
                <span className="whitespace-nowrap text-[13px] tabular-nums" style={{ color: 'var(--content-muted)' }}>
                  {row.original.releaseDate ?? '—'}
                </span>
              ),
            } satisfies ColumnDef<ProductSummary>,
          ]
        : []),
      ...(isColumnVisible('createdAt')
        ? [
            {
              id: 'createdAt',
              header: () => t('products.columns.createdUpdated', '등록일/수정일'),
              cell: ({ row }) => {
                // ProductSummaryView.createdAt/updatedAt(Instant, catalog fceac51). generated 미반영 → 안전 캐스트.
                const r = row.original as {
                  createdAt?: string | null;
                  updatedAt?: string | null;
                  createdBy?: string | null;
                  updatedBy?: string | null;
                };
                return (
                  // 등록일/수정일 모두 YYYY-MM-DD(QA-305, 오늘도 날짜로) — 한 줄씩 두 줄. 시각은 마우스를 올리면 title 로.
                  // QA-515 날짜 옆에 등록자·수정자(관리자 이름). 기록이 없으면 날짜만.
                  <div className="flex flex-col whitespace-nowrap text-[12px] leading-tight tabular-nums" style={{ color: 'var(--content-muted)' }}>
                    <span title={formatAuditTime(r.createdAt) ?? undefined} data-testid="product-created-cell">
                      {formatAuditDate(r.createdAt) ?? '—'}
                      {r.createdBy ? (
                        <>
                          {' · '}
                          <AdminNameCell adminUserId={r.createdBy} />
                        </>
                      ) : null}
                    </span>
                    <span title={formatAuditTime(r.updatedAt) ?? undefined} data-testid="product-updated-cell">
                      {formatAuditDate(r.updatedAt) ?? '—'}
                      {r.updatedBy ? (
                        <>
                          {' · '}
                          <AdminNameCell adminUserId={r.updatedBy} />
                        </>
                      ) : null}
                    </span>
                  </div>
                );
              },
            } satisfies ColumnDef<ProductSummary>,
          ]
        : []),
      ...(isColumnVisible('totalStockQty')
        ? [
            {
              id: 'totalStockQty',
              header: () => t('products.columns.stockQty', '재고 수량'),
              cell: ({ row }) => {
                // be-stock: ProductSummaryView.totalStockQty = 활성(active) 옵션 stock_qty 합 (판매가능 재고).
                // admin 목록만 채움(공개 목록·구 빌드는 null → "—"). active-only 라 isSoldOut 뱃지와 일관
                // (활성옵션 0 → 품절+0). 비활성 옵션 개별재고는 상품상세 variants[]. generated 미반영 → 안전 캐스트.
                const r = row.original as {
                  totalStockQty?: number | null;
                  unlimitedStock?: boolean | null;
                };
                // QA-244: 재고추적 OFF(무한판매) 상품은 0(품절)과 구분이 안 됐다 → ∞ + '무제한' 라벨로 표기.
                // BE ProductSummaryView.unlimitedStock(활성 옵션이 모두 재고추적 OFF) true 일 때 우선 적용.
                if (r.unlimitedStock) {
                  return (
                    <span
                      className="whitespace-nowrap text-[13px] tabular-nums"
                      style={{ color: 'var(--content-default)' }}
                      title={t('products.columns.stockUnlimited', '무제한')}
                    >
                      ∞ {t('products.columns.stockUnlimited', '무제한')}
                    </span>
                  );
                }
                const qty = r.totalStockQty;
                return (
                  <span
                    className="whitespace-nowrap text-[13px] tabular-nums"
                    style={{ color: row.original.isSoldOut ? 'var(--signal-danger)' : 'var(--content-default)' }}
                  >
                    {qty == null ? '—' : new Intl.NumberFormat('ko-KR').format(qty)}
                  </span>
                );
              },
            } satisfies ColumnDef<ProductSummary>,
          ]
        : []),
      ...(isColumnVisible('isSoldOut')
        ? [
            {
              id: 'isSoldOut',
              header: () => t('products.columns.stockState'),
              cell: ({ row }) => (
                <span className="whitespace-nowrap">
                  <StatusDot
                    tone={row.original.isSoldOut ? 'danger' : 'success'}
                    label={
                      row.original.isSoldOut
                        ? t('products.status.soldOut')
                        : t('products.status.inStock')
                    }
                  />
                </span>
              ),
            } satisfies ColumnDef<ProductSummary>,
          ]
        : []),
      // QA-R6 H: 행 우측 상품 복사 — 노출 항목 토글('copy', 기본 ON)로 켜고 끈다(copyable=onCopy && 'copy' 노출).
      // 컬럼 id 를 'copy' 로 두어 COLUMN_MIN_WIDTH.copy(70) 폭 산식·노출항목 키와 정합.
      ...(copyable
        ? [
            {
              id: 'copy',
              header: () => null,
              cell: ({ row }) => {
                const id = row.original.productId;
                return (
                  <div
                    className="flex items-center justify-end"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      disabled={copyingId === id}
                      onClick={() => onCopy?.(id)}
                      className="whitespace-nowrap px-2"
                      title={t('products.copy.action', '상품 복사')}
                      aria-label={t('products.copy.rowAction', {
                        name: row.original.name,
                        defaultValue: t('products.copy.action', '상품 복사'),
                      })}
                    >
                      <Copy size={14} aria-hidden="true" />
                      {copyingId === id
                        ? t('products.copy.copying', '복사 중…')
                        : t('products.copy.short', '복사')}
                    </Button>
                  </div>
                );
              },
            } satisfies ColumnDef<ProductSummary>,
          ]
        : []),
    ],
    [t, navigate, selectable, selectedSet, allSelected, pageIds, onToggleRow, onToggleAll, copyable, copyingId, onCopy, isColumnVisible],
  );

  const table = useReactTable({
    data: items as ProductSummary[],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  // QA-89(R4 G): 표 min-width = 실제 렌더되는 컬럼들의 content-fit 폭 합(design-o 가중치).
  // 컬럼 적으면 합<가용폭→100% 유지(스크롤X), 많으면 합>가용폭→.table-scroll-x 가로 스크롤.
  const tableMinWidth = useMemo(
    () =>
      columns.reduce(
        (sum, col) => sum + (COLUMN_MIN_WIDTH[col.id ?? ''] ?? DEFAULT_COLUMN_MIN_WIDTH),
        0,
      ),
    [columns],
  );

  // QA-R6 H: 가로 스크롤바가 표 하단에만 있어 상단에서 조절이 안 되던 문제 → 표 위에 동기화된
  // 더미 스크롤 트랙을 둔다(design2 스펙: ProductTable 로컬, 공용 추출은 2번째 수요 시 — 추출 후보).
  // 본문(.table-scroll-x)과 상단 트랙(.table-scroll-x-top)의 scrollLeft 를 양방향 동기화하되
  // 재진입 가드(syncingRef)로 무한 루프를 막는다. 가로 오버플로가 실제로 있을 때만 노출(빈 트랙 노이즈 방지).
  const bodyScrollRef = useRef<HTMLDivElement>(null);
  const topScrollRef = useRef<HTMLDivElement>(null);
  const syncingRef = useRef(false);
  const [hasOverflow, setHasOverflow] = useState(false);

  const syncScroll = useCallback((from: 'top' | 'body') => {
    const top = topScrollRef.current;
    const body = bodyScrollRef.current;
    if (!top || !body) return;
    if (syncingRef.current) return;
    syncingRef.current = true;
    if (from === 'top') body.scrollLeft = top.scrollLeft;
    else top.scrollLeft = body.scrollLeft;
    // rAF 로 가드 해제 — 동기 설정으로 발생한 상대편 onScroll 을 한 프레임 무시.
    requestAnimationFrame(() => {
      syncingRef.current = false;
    });
  }, []);

  // 컨테이너 실폭 대비 표 폭(min-width)이 커서 가로 스크롤이 생기는지 관측. 컬럼 토글/리사이즈에 반응.
  useEffect(() => {
    const body = bodyScrollRef.current;
    if (!body) return;
    const measure = () => setHasOverflow(body.scrollWidth > body.clientWidth + 1);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(body);
    return () => ro.disconnect();
  }, [tableMinWidth, items.length]);

  if (items.length === 0) {
    return (
      <div
        role="status"
        aria-live="polite"
        className="flex flex-col items-center gap-2 rounded-lg py-16 text-center"
        style={{
          background: 'var(--surface-raised)',
          border: '1px solid var(--divider-strong)',
          color: 'var(--content-muted)',
        }}
      >
        <ImageOff size={28} aria-hidden="true" />
        <p className="text-[14px]">{t('products.list.empty')}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      {/* QA-R6 H: 상단 동기화 가로 스크롤바 — 본문 표와 동일 폭의 빈 트랙. 가로 오버플로가 있을 때만 노출.
          aria-hidden(보조 스크롤 UI). 스타일/스크롤바는 globals.css .table-scroll-x-top 가 .table-scroll-x 와 공유. */}
      {hasOverflow ? (
        <div
          ref={topScrollRef}
          className="table-scroll-x-top"
          aria-hidden="true"
          onScroll={() => syncScroll('top')}
        >
          <div style={{ width: `${tableMinWidth}px` }} />
        </div>
      ) : null}
      {/* QA-89: 노출 컬럼을 늘려 표가 PC 가로 폭을 넘으면 가로 스크롤바로 이동할 수 있게 한다.
          기존 `overflow-hidden` 이 내부 Table 의 스크롤을 잘라 드래그로만 이동되던 문제를
          `table-scroll-x`(overflow-x:auto + 토큰 스크롤바, globals.css) 로 교체. */}
      <div
        ref={bodyScrollRef}
        onScroll={() => syncScroll('body')}
        className="table-scroll-x rounded-lg"
        style={{
          background: 'var(--surface-raised)',
          border: '1px solid var(--divider-strong)',
        }}
      >
      {/* QA-89(R4 G/H): 컬럼이 늘어 합산 폭이 컨테이너를 넘으면 위 .table-scroll-x 가 가로 스크롤을 노출.
          ★단 표가 w-full 이고 상품명 셀이 truncate+min-w-0(축소 허용)이라, min-width 없이 두면
          컬럼이 많아도 상품명이 줄며 100%에 압축돼 영영 오버플로가 안 생긴다(=드래그-only 회귀).
          그래서 표에 **노출 컬럼들의 content-fit 폭 합**(tableMinWidth, COLUMN_MIN_WIDTH=design-o 가중치)을
          min-width 로 준다. max-content 가 아니라 컬럼별 고정폭 합이라 긴 옵션값이 표를 과도하게
          늘리지 않으면서, 컬럼이 많아지면 합>가용폭→스크롤. 적으면 합<가용폭→종전처럼 100% 유지
          (flat 150/col 은 기본 10컬럼서 과도 스크롤이라 가중치로 교체). 폭/트리거=fe-bo-o(컬럼),
          스크롤바 스타일=design-o(.table-scroll-x). */}
      <Table data-testid="product-table" style={{ minWidth: `${tableMinWidth}px` }}>
        <TableHeader style={{ background: 'var(--surface-1)' }}>
          {table.getHeaderGroups().map((group) => (
            <TableRow key={group.id}>
              {group.headers.map((header) => {
                const isSortable = SORTABLE_COLUMNS.has(header.column.id);
                const isActive = activeSortId === header.column.id;
                const ariaSort: 'ascending' | 'descending' | 'none' = isSortable
                  ? isActive
                    ? activeSortDir === 'ASC'
                      ? 'ascending'
                      : 'descending'
                    : 'none'
                  : 'none';
                const align = header.column.id === 'salePrice' ? 'right' : 'left';
                const sticky = stickyColClass(header.column.id);
                return (
                  <TableHead
                    key={header.id}
                    scope="col"
                    aria-sort={isSortable ? ariaSort : undefined}
                    className={[sticky, NOWRAP_HEADER_COLUMNS.has(header.column.id) ? 'whitespace-nowrap' : '']
                      .filter(Boolean)
                      .join(' ') || undefined}
                    style={{
                      textAlign: align,
                      // QA-244: 상품명 sticky 열은 고정 width 대신 minWidth(280)만 부여해 growable.
                      // (긴 상품명이 더 보이도록 확대 — COLUMN_MIN_WIDTH.name 정합. width 제거로 여유 폭 흡수.)
                      ...(header.column.id === 'name' ? { minWidth: COLUMN_MIN_WIDTH.name } : {}),
                      // QA-348: 머리글에도 컬럼 최소 폭을 준다(종전엔 표 전체 폭 계산에만 써서 짧은 칸이 좁아졌다).
                      ...(NOWRAP_HEADER_COLUMNS.has(header.column.id) && COLUMN_MIN_WIDTH[header.column.id]
                        ? { minWidth: COLUMN_MIN_WIDTH[header.column.id] }
                        : {}),
                    }}
                  >
                    {isSortable ? (
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 focus-visible:outline-none focus-visible:ring-2"
                        onClick={() => onSortChange(toggleSort(sort, header.column.id))}
                        aria-label={t('products.list.sortBy', {
                          column: t(`products.columns.${header.column.id}`),
                        })}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        <span aria-hidden="true">
                          {isActive ? (activeSortDir === 'ASC' ? '↑' : '↓') : ''}
                        </span>
                      </button>
                    ) : (
                      flexRender(header.column.columnDef.header, header.getContext())
                    )}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.map((row) => (
            // QA-204: 행 전체가 링크(role="link"+onClick)였던 것을 제거 — 행 어디서나
            // 커서 활성/드래그 불가 문제. 수정 진입은 상품명 버튼(아래 name 셀)만 담당.
            <TableRow
              key={row.id}
              data-testid="product-row"
              className="transition-colors hover:bg-[color:var(--surface-1)]"
            >
              {row.getVisibleCells().map((cell) => {
                const sticky = stickyColClass(cell.column.id);
                return (
                <TableCell
                  key={cell.id}
                  className={sticky || undefined}
                  style={{
                    ...(cell.column.id === 'salePrice' ? { textAlign: 'right' as const } : {}),
                    // QA-244: 상품명 셀 growable — 고정 width 제거, minWidth(280)만. 2줄 clamp 유지, 상단 정렬.
                    ...(cell.column.id === 'name'
                      ? { minWidth: COLUMN_MIN_WIDTH.name, verticalAlign: 'top' as const }
                      : {}),
                  }}
                  data-testid={cell.column.id === 'artistName' ? 'product-artist-column' : undefined}
                >
                  {flexRender(
                    cell.column.columnDef.cell ?? ((ctx) => ctx.getValue() as string),
                    cell.getContext(),
                  )}
                </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      </div>
    </div>
  );
};
